import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const readLS = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [settings, setSettings] = useState({ whatsapp_number: "918595672124" });
  const [cart, setCart] = useState(() => readLS("ln_cart", []));
  const [wishlist, setWishlist] = useState(() => readLS("ln_wishlist", []));

  useEffect(() => { localStorage.setItem("ln_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("ln_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    const token = localStorage.getItem("ln_token");
    if (token) {
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("ln_token")).finally(() => setAuthReady(true));
    } else setAuthReady(true);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ln_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("ln_token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = () => {
    localStorage.removeItem("ln_token");
    setUser(null);
    api.post("/auth/logout").catch(() => {});
  };

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const ex = prev.find((i) => i.product_id === product.id);
      if (ex) return prev.map((i) => i.product_id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, {
        product_id: product.id, name: product.name, price: product.price, mrp: product.mrp,
        qty, image: product.images?.[0] || "", sku: product.sku, pack_size: product.pack_size, slug: product.slug,
      }];
    });
    toast.success(`${product.name} added to cart`);
  }, []);

  const updateQty = (pid, qty) => setCart((p) => p.map((i) => i.product_id === pid ? { ...i, qty: Math.max(1, qty) } : i));
  const removeFromCart = (pid) => setCart((p) => p.filter((i) => i.product_id !== pid));
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      if (prev.find((i) => i.id === product.id)) {
        toast("Removed from wishlist");
        return prev.filter((i) => i.id !== product.id);
      }
      toast.success("Added to wishlist");
      return [...prev, { id: product.id, name: product.name, price: product.price, mrp: product.mrp, images: product.images, slug: product.slug, pack_size: product.pack_size, brand: product.brand }];
    });
  };
  const inWishlist = (id) => wishlist.some((i) => i.id === id);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <AppContext.Provider value={{
      user, setUser, authReady, login, register, logout, settings, setSettings,
      cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, subtotal,
      wishlist, toggleWishlist, inWishlist, formatApiError,
    }}>
      {children}
    </AppContext.Provider>
  );
}
